import {
  GlobalOutlined,
  PlusOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useTenantBranches } from "../../../../../hooks/useTenantBranches";
import Modal from "../../../../../components/generic/Modal";
import { Button, Form, Input, message, Segmented, Spin } from "antd";
import Select from "../../../../../components/generic/Select";
import { useCallback, useEffect, useMemo, useState } from "react";
import PhoneInput from "../../../../../components/shared/PhoneInput";
import { useDispatch, useSelector } from "react-redux";
import { createConversation } from "../../../../../store/chats/thunks";
import httpClient from "../../../../../api/httpClient";
import CustomCollapse from "../../../../../components/generic/CustomDetails";
import RecentConversation from "./RecentConversation";
import { StyledCreateConversation } from "./styles";
import { setKeyValue } from "../../../../../store/chats/slice";
import MemberSelect from "../../../../../components/shared/MemberSelect";
import { useTenantUsers } from "../../../../../hooks/useTenantUsers";
import { selectUser } from "../../../../../store/user/selectors";

const CreateConversation = ({ topics, chats }) => {
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(null);
  const user = useSelector(selectUser);
  const { users } = useTenantUsers({ fetchUsers: false });
  const {
    branchOptions,
    loading: branchLoading,
    branches,
    selectedTenantId,
  } = useTenantBranches();

  const [form] = Form.useForm();
  const dispatch = useDispatch();

  useEffect(() => {
    const mainBranch = branches?.find((b) => b.is_main_branch);
    if (mainBranch && showModal)
      form.setFieldValue("branch_id", mainBranch?.id);
  }, [branches, showModal]);
  const handleBranchChange = useCallback(() => {
    form.setFieldsValue({ topic_ids: [] });
  }, [form]);

  const selectedBranchId = Form.useWatch("branch_id", form);

  const topicsOptions = useMemo(() => {
    if (!selectedBranchId) return [];

    const options = (
      topics?.filter((t) => t?.branch_ids?.includes(selectedBranchId)) || []
    )?.map(({ name, id }) => ({ label: name, value: id }));
    form.setFieldsValue({
      topic_id: options?.length ? options[0]?.value : undefined,
    });
    return options;
  }, [topics, selectedBranchId]);

  const onSubmit = useCallback(
    async (values) => {
      try {
        setSubmitting(true);

        if (values?.advisor_id) {
          const memberIds = new Set([user?.user_id, values?.advisor_id]);

          const alreadyChat = chats?.find(({ customer, members = [] }) => {
            return (
              !customer &&
              members?.length === 2 &&
              members?.every(({ id }) => memberIds.has(id))
            );
          });

          if (alreadyChat) {
            dispatch(
              setKeyValue({
                key: "selectedChatId",
                value: alreadyChat?.id,
              }),
            );

            setShowModal(false);
            return;
          }
        }

        const { payload } = await dispatch(
          createConversation({
            ...values,
            ...values.mobile_number,
            tenant_id: selectedTenantId,
            type: "create",
          }),
        );
        if (payload?.tenant_id) {
          form.resetFields();

          dispatch(
            setKeyValue({
              key: "selectedCaseForm",
              value: {
                id: "create_case",
              },
            }),
          );

          setShowModal(false);
        }
      } catch (error) {
        console.log(
          "🚀 ~ CreateConversation.jsx:42 ~ CreateConversation ~ error:",
          error,
        );
      } finally {
        setSubmitting(false);
      }
    },
    [selectedTenantId, chats, user],
  );

  const onSearch = useCallback(async () => {
    try {
      setSearching(true);
      const phone = form.getFieldValue(["mobile_number", "phone"]);
      const phone_country_code = form.getFieldValue([
        "mobile_number",
        "phone_country_code",
      ]);

      if (!phone) {
        message.warning("Please enter a phone number");
        return;
      }

      const { data } = await httpClient.get("customers", {
        params: {
          tenant_id: selectedTenantId,
          phone,
          phone_country_code,
        },
      });

      const customer = data?.[0];

      setCustomer(customer);

      if (customer) {
        const { first_name = "", last_name = "", email = "" } = customer;

        form.setFieldsValue({
          customer_name: `${first_name} ${last_name || ""}`.trim(),
          email,
        });

        message.success("Customer found!");
      } else {
        form.setFieldsValue({
          customer_name: "",
          email: "",
        });
        message.info("No customer found with this phone number");
      }
    } catch (error) {
      message.error("Failed to search customer");
    } finally {
      setSearching(false);
    }
  }, [form, selectedTenantId]);

  const customerChats = useMemo(
    () =>
      chats?.filter((c) => c?.customer && c?.customer?.id === customer?.id) ||
      [],
    [chats, customer],
  );

  const type = Form.useWatch("type", form);

  const onCancel = useCallback(() => {
    setShowModal(false);
    form.resetFields();
    setCustomer(null);
  }, []);

  return (
    <>
      <span
        className="cursor-pointer create-conversation-btn"
        onClick={() => setShowModal(true)}
      >
        <PlusOutlined size={50} />
      </span>
      {showModal && (
        <Modal
          title="Start New Conversation"
          open
          onCancel={onCancel}
          onOk={() => form.submit()}
          isProcessing={submitting}
        >
          <StyledCreateConversation>
            <Spin spinning={searching} tip="Searching customer...">
              <Form
                layout="vertical"
                form={form}
                onFinish={onSubmit}
                disabled={submitting}
                initialValues={{
                  type: "external",
                }}
                style={{ marginTop: "20px" }}
              >
                <Form.Item label="Type" name="type">
                  <Segmented
                    block
                    options={[
                      {
                        label: "External",
                        value: "external",
                        icon: <GlobalOutlined />,
                      },
                      {
                        label: "Internal",
                        value: "internal",
                        icon: <TeamOutlined />,
                      },
                    ]}
                  />
                </Form.Item>

                {type === "internal" ? (
                  <>
                    <Form.Item label="Users" name="advisor_id">
                      <MemberSelect
                        users={users?.filter(({ id }) => id !== user?.user_id)}
                        //@ts-ignore
                        mode="single"
                        placeholder="Select Users"
                      />
                    </Form.Item>
                  </>
                ) : (
                  <>
                    <PhoneInput split />
                    <div className="w-100 flex justify-end">
                      <Button
                        size="small"
                        icon={<SearchOutlined />}
                        type="primary"
                        onClick={onSearch}
                        loading={searching}
                        disabled={searching || submitting}
                      >
                        Find Customer
                      </Button>
                    </div>
                    {!!customerChats?.length && (
                      <CustomCollapse title="Recent Conversations" open>
                        <div className="chats-list">
                          {customerChats.map((item, i) => (
                            <RecentConversation
                              key={i}
                              item={item}
                              onCancel={onCancel}
                            />
                          ))}
                        </div>
                      </CustomCollapse>
                    )}
                    <Form.Item
                      name="customer_name"
                      label="Customer Name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter customer name",
                        },
                      ]}
                    >
                      <Input placeholder="Enter Customer Name" />
                    </Form.Item>
                    <Form.Item
                      label="Email"
                      name="email"
                      rules={[
                        { type: "email", message: "Invalid email format!" },
                      ]}
                    >
                      <Input placeholder="Enter your email" />
                    </Form.Item>
                    <Form.Item
                      name="branch_id"
                      label="Branch"
                      rules={[
                        { required: true, message: "Please select a branch" },
                      ]}
                    >
                      <Select
                        placeholder="Select branch"
                        options={branchOptions}
                        onChange={handleBranchChange}
                        loading={branchLoading}
                      />
                    </Form.Item>
                    <Form.Item
                      name="topic_id"
                      label="Topics"
                      rules={[
                        {
                          required: true,
                          message: "Please select topic",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select topics"
                        options={topicsOptions}
                      />
                    </Form.Item>
                  </>
                )}
              </Form>
            </Spin>
          </StyledCreateConversation>
        </Modal>
      )}
    </>
  );
};

export default CreateConversation;
